import qs from 'qs';
import { axiosClient } from '@/libs/axios-client';
import {
  customUrlEncode,
  prepareFilters,
  preparePagination,
  prepareSorters,
} from '@/utils/apiParamsUtils';

/**
 * Creates a new support ticket.
 *
 * Sends a POST request to `/supports/create` with the ticket data.
 *
 * @param {Object} payload - The support ticket data.
 * @param {string} payload.subject - The ticket subject.
 * @param {string} payload.message - The ticket message.
 * @param {string} payload.type - The inquiry type (general, support, feedback, other).
 * @returns {Promise<Object>} A promise that resolves with the created ticket response (including ticketNumber).
 * @throws {Error} Throws an error if the API call fails.
 */
export const createSupportTicket = async (payload) => {
  try {
    const response = await axiosClient.post('/supports/create', payload, {
      headers: { 'Content-Type': 'application/json' },
    });

    return response.data;
  } catch (error) {
    console.error('Failed to create support ticket:', error);
    throw error;
  }
};

/**
 * Retrieves all support tickets with filtering, sorting, and pagination.
 *
 * @param {Object} params - Query parameters for filtering, sorting, and pagination.
 * @returns {Promise<Object>} A promise that resolves with support tickets data.
 * @throws {Error} Will throw an error if the request fails.
 */
export const retrieveAllSupports = async (params) => {
  const filters = prepareFilters(params, {
    subject: 'default',
    ticketNumber: 'default',
    type: 'array',
    status: 'array',
    createdAt: 'range',
    updatedAt: 'range',
    'user.firstName': 'default',
    'user.lastName': 'default',
    'user.email': 'default',
    'user.roles': 'array',
  });

  const sorters = prepareSorters(params, {
    ticketNumber: 'sortTicketNumber',
    subject: 'sortSubject',
    type: 'sortType',
    status: 'sortStatus',
    'user.firstName': 'sortUser.firstName',
    'user.lastName': 'sortUser.lastName',
    'user.email': 'sortUser.email',
    'user.roles': 'sortUser.roles',
    createdAt: 'sortCreatedAt',
  });

  const pagination = preparePagination(params);

  try {
    const response = await axiosClient.get('/supports', {
      params: {
        filter: filters,
        sorter: sorters,
        ...pagination,
      },
      paramsSerializer: (params) => {
        if (params.filter) {
          params.filter = JSON.stringify(params.filter);
        }

        if (params.sorter) {
          params.sorter = JSON.stringify(params.sorter);
        }

        return qs.stringify(params, {
          encode: true,
          arrayFormat: 'brackets',
          encoder: customUrlEncode,
        });
      },
    });
    return response.data;
  } catch (error) {
    console.error('Failed to retrieve support tickets:', error);
    throw error;
  }
};

/**
 * Retrieves a single support ticket by its ID.
 *
 * @param {string} supportId - The unique identifier of the support ticket.
 * @returns {Promise<Object>} A promise that resolves with the support ticket data.
 * @throws {Error} Will throw an error if the request fails.
 */
export const getSupportById = async (supportId) => {
  try {
    const response = await axiosClient.get(`/supports/${supportId}`);
    const { data } = response.data;
    return data;
  } catch (error) {
    console.error(
      `Failed to retrieve support ticket with ID ${supportId}:`,
      error
    );
    throw error;
  }
};

/**
 * Updates a support ticket by its ID.
 * Can update message (adds to reply thread) and/or status.
 *
 * @param {string} supportId - The unique identifier of the support ticket.
 * @param {Object} payload - The update data.
 * @param {string} [payload.message] - New message to add to the conversation thread.
 * @param {string} [payload.status] - New status (in progress, solved, reminder, archived).
 * @returns {Promise<Object>} A promise that resolves with the updated ticket response.
 * @throws {Error} Throws an error if the request fails.
 */
export const updateSupportById = async (supportId, payload) => {
  try {
    const response = await axiosClient.put(`/supports/${supportId}`, payload, {
      headers: { 'Content-Type': 'application/json' },
    });

    return response.data;
  } catch (error) {
    console.error(
      `Failed to update support ticket with ID ${supportId}:`,
      error
    );
    throw error;
  }
};

/**
 * Uploads attachments for a specific support ticket message.
 *
 * @param {string} ticketId - The unique identifier of the support ticket.
 * @param {string} messageId - The unique identifier of the message within the ticket.
 * @param {File[]} files - Array of File objects to upload (max 5 files, 100MB each).
 * @returns {Promise<Object>} A promise that resolves with the uploaded attachments data.
 * @throws {Error} Throws an error if the request fails.
 */
export const uploadSupportAttachments = async (ticketId, messageId, files) => {
  try {
    const formData = new FormData();

    // Append all files to formData
    files.forEach((file) => {
      formData.append('files', file);
    });

    const response = await axiosClient.post(
      `/supports/${ticketId}/messages/${messageId}/attachments`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error(
      `Failed to upload attachments for ticket ${ticketId}, message ${messageId}:`,
      error
    );
    throw error;
  }
};

/**
 * Gets the count of support tickets with unread messages for the current user.
 *
 * @returns {Promise<number>} The unread support ticket count.
 * @throws {Error} Will throw an error if the request fails.
 */
export const getUnreadSupportCount = async () => {
  try {
    const response = await axiosClient.get('/supports/unread-count');
    return response.data.unreadCount;
  } catch (error) {
    console.error('Failed to get unread support count:', error);
    throw error;
  }
};

const SupportsService = {
  createSupportTicket,
  retrieveAllSupports,
  getSupportById,
  updateSupportById,
  uploadSupportAttachments,
  getUnreadSupportCount,
};

export default SupportsService;
